(function () {
  const config = window.MRJCurrencyConfig || {};
  const priceSelectors = [
    '.yv-product-price', '.yv-product-compare-price', '.yv-product-compare-value', '.yv-compare-price',
    '.yv-spotlight-product-price', '[data-cart-total-price]',
    '[data-cart-original-price]', '[data-unit-price]', '[data-mrj-base-minor]'
  ].join(',');
  const selectorQuery = '[data-mrj-currency-selector]';
  const snapshotCacheKey = 'MRJCurrencySnapshot';
  const preferenceKey = 'MRJCurrencyPreference';
  const fetchTimeoutMs = 3000;
  let snapshot;
  let currentContext;
  let manualPreference;
  let preferenceLoaded = false;
  let processing = false;
  let observerStarted = false;
  let outsideClickStarted = false;

  function revealPrices() {
    if (window.MRJCurrencyRevealTimer) {
      window.clearTimeout(window.MRJCurrencyRevealTimer);
      window.MRJCurrencyRevealTimer = null;
    }
    document.documentElement.classList.remove('mrj-currency-pending');
    document.documentElement.classList.add('mrj-currency-ready');
  }

  function isValidSnapshot(value) {
    if (!value || typeof value !== 'object') return false;
    if (config.snapshotId && value.id !== config.snapshotId) return false;
    if (!value.rates || typeof value.rates !== 'object') return false;
    return true;
  }

  function cachedSnapshot() {
    if (!config.snapshotId) return null;
    try {
      const value = JSON.parse(window.localStorage.getItem(snapshotCacheKey));
      return isValidSnapshot(value) ? value : null;
    } catch (error) {
      return null;
    }
  }

  function cacheSnapshot(value) {
    if (!config.snapshotId) return;
    try {
      window.localStorage.setItem(snapshotCacheKey, JSON.stringify(value));
    } catch (error) {
      // Currency conversion must continue when storage is unavailable.
    }
  }

  function effectiveSnapshot(value) {
    if (value.source === 'fixer' && value.publishedAt && Date.now() - new Date(value.publishedAt).getTime() > 72 * 60 * 60 * 1000) {
      return Object.assign({}, value, {
        baseCurrency: 'INR',
        rates: { INR: '1' },
        countryCurrencies: {},
        country_currencies: {}
      });
    }
    return value;
  }

  function baseCurrency(value) {
    return String(value.baseCurrency || config.storeCurrency || 'INR').toUpperCase();
  }

  function rateFor(value, currencyCode) {
    const code = String(currencyCode || '').toUpperCase();
    if (code === baseCurrency(value)) return 1;
    const rate = Number((value.rates || {})[code]);
    return Number.isFinite(rate) && rate > 0 ? rate : null;
  }

  function supportedCurrencyCodes(value) {
    const codes = new Set([
      ...Object.keys(value.currencies || {}),
      ...Object.keys(value.rates || {}),
      baseCurrency(value)
    ]);
    return Array.from(codes)
      .map((code) => String(code).toUpperCase())
      .filter((code) => /^[A-Z]{3}$/.test(code) && rateFor(value, code) !== null)
      .sort();
  }

  function readPreference() {
    if (preferenceLoaded) return manualPreference;
    preferenceLoaded = true;
    try {
      const value = String(window.localStorage.getItem(preferenceKey) || '').toUpperCase();
      manualPreference = /^[A-Z]{3}$/.test(value) ? value : null;
    } catch (error) {
      manualPreference = null;
    }
    return manualPreference;
  }

  function setPreference(currencyCode) {
    manualPreference = currencyCode ? String(currencyCode).toUpperCase() : null;
    preferenceLoaded = true;
    try {
      if (manualPreference) window.localStorage.setItem(preferenceKey, manualPreference);
      else window.localStorage.removeItem(preferenceKey);
    } catch (error) {
      // The current-page selection still works when storage is unavailable.
    }
  }

  function automaticCurrency(value) {
    const countryCurrencies = value.countryCurrencies || value.country_currencies || {};
    const mapped = String(countryCurrencies[String(config.countryCode || '').toUpperCase()] || '').toUpperCase();
    if (rateFor(value, mapped) !== null) return mapped;
    const base = baseCurrency(value);
    if (rateFor(value, base) !== null) return base;
    if (rateFor(value, 'INR') !== null) return 'INR';
    return supportedCurrencyCodes(value)[0] || 'INR';
  }

  function contextFor(value) {
    const supported = supportedCurrencyCodes(value);
    let preferred = readPreference();
    if (preferred && supported.indexOf(preferred) === -1) {
      setPreference(null);
      preferred = null;
    }
    const currencyCode = preferred || automaticCurrency(value);
    return {
      currencyCode: currencyCode,
      rate: rateFor(value, currencyCode) || 1,
      currency: (value.currencies || {})[currencyCode],
      selectionMode: preferred ? 'manual' : 'auto',
      automaticCurrencyCode: automaticCurrency(value)
    };
  }

  function currencySymbol(currencyCode, currency) {
    const configuredSymbol = currency && typeof currency.symbol === 'string' ? currency.symbol.trim() : '';
    if (configuredSymbol) return configuredSymbol;
    try {
      const part = new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol'
      }).formatToParts(0).find((item) => item.type === 'currency');
      return part ? part.value : '';
    } catch (error) {
      return '';
    }
  }

  function currencyLabel(currencyCode, value) {
    const symbol = currencySymbol(currencyCode, (value.currencies || {})[currencyCode]);
    return symbol && symbol !== currencyCode ? currencyCode + ' (' + symbol + ')' : currencyCode;
  }

  function format(amount, currencyCode, currency) {
    const symbol = currency && typeof currency.symbol === 'string' ? currency.symbol.trim() : '';
    const decimalPlaces = currency && Number.isInteger(currency.decimalPlaces)
      ? currency.decimalPlaces
      : -1;
    if (symbol && decimalPlaces >= 0 && decimalPlaces <= 20) {
      const number = new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'decimal',
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces
      }).format(amount);
      return symbol + number;
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
      style: 'currency', currency: currencyCode,
      minimumFractionDigits: currencyCode === 'INR' ? 0 : undefined,
      maximumFractionDigits: currencyCode === 'INR' ? 0 : undefined
    }).format(amount);
  }

  function convertElement(element, context) {
    const baseMinor = Number(element.dataset.mrjBaseMinor);
    if (!Number.isInteger(baseMinor) || baseMinor < 0) return;
    const rendered = format(baseMinor / 100 * context.rate, context.currencyCode, context.currency);
    if (element.textContent !== rendered) element.textContent = rendered;
    element.dataset.mrjRendered = rendered;
    element.dataset.mrjCurrency = context.currencyCode;
  }

  function apply(root) {
    if (!snapshot || !currentContext || processing) return;
    processing = true;
    try {
      const elements = [];
      if (root instanceof Element && root.matches(priceSelectors)) elements.push(root);
      if (root.querySelectorAll) elements.push(...root.querySelectorAll(priceSelectors));
      elements.forEach((element) => convertElement(element, currentContext));
    } finally {
      processing = false;
    }
  }

  function closeSelector(control, restoreFocus) {
    const trigger = control.querySelector('[data-mrj-currency-trigger]');
    const menu = control.querySelector('[data-mrj-currency-menu]');
    control.classList.remove('active');
    trigger.setAttribute('aria-expanded', 'false');
    menu.style.display = 'none';
    if (restoreFocus) trigger.focus();
  }

  function closeOtherSelectors(current) {
    document.querySelectorAll(selectorQuery + '.active').forEach((control) => {
      if (control !== current) closeSelector(control, false);
    });
  }

  function focusOption(control, direction) {
    const options = Array.from(control.querySelectorAll('[data-mrj-currency-option]'));
    if (!options.length) return;
    const focusedIndex = options.indexOf(document.activeElement);
    const selectedIndex = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    let nextIndex = focusedIndex;
    if (direction === 'first') nextIndex = 0;
    else if (direction === 'last') nextIndex = options.length - 1;
    else if (focusedIndex === -1) nextIndex = selectedIndex === -1 ? 0 : selectedIndex;
    else nextIndex = (focusedIndex + direction + options.length) % options.length;
    options[nextIndex].focus();
  }

  function openSelector(control, focusDirection) {
    closeOtherSelectors(control);
    control.classList.add('active');
    control.querySelector('[data-mrj-currency-trigger]').setAttribute('aria-expanded', 'true');
    control.querySelector('[data-mrj-currency-menu]').style.display = 'block';
    if (focusDirection) focusOption(control, focusDirection);
  }

  function selectCurrency(value, selectedControl) {
    setPreference(value === 'auto' ? null : value);
    refresh(true);
    document.querySelectorAll(selectorQuery).forEach((control) => closeSelector(control, false));
    if (selectedControl && document.contains(selectedControl)) {
      selectedControl.querySelector('[data-mrj-currency-trigger]').focus();
    }
  }

  function createOption(control, value, label, selected) {
    const item = document.createElement('li');
    const button = document.createElement('button');
    item.className = 'country-list-item';
    button.type = 'button';
    button.className = 'custom-checkbox radio-box body-font';
    button.setAttribute('role', 'option');
    button.setAttribute('aria-selected', selected ? 'true' : 'false');
    button.dataset.mrjCurrencyOption = value;
    button.textContent = label;
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      selectCurrency(value, control);
    });
    item.appendChild(button);
    control.querySelector('[data-mrj-currency-options]').appendChild(item);
  }

  function renderSelector(control) {
    if (!snapshot || !currentContext) return;
    const trigger = control.querySelector('[data-mrj-currency-trigger]');
    const options = control.querySelector('[data-mrj-currency-options]');
    options.replaceChildren();
    trigger.disabled = false;
    trigger.querySelector('[data-mrj-currency-trigger-label]').textContent = currencyLabel(currentContext.currencyCode, snapshot);
    createOption(
      control,
      'auto',
      'Auto \u2014 ' + currencyLabel(currentContext.automaticCurrencyCode, snapshot),
      currentContext.selectionMode === 'auto'
    );
    supportedCurrencyCodes(snapshot).forEach((currencyCode) => {
      createOption(
        control,
        currencyCode,
        currencyLabel(currencyCode, snapshot),
        currentContext.selectionMode === 'manual' && currentContext.currencyCode === currencyCode
      );
    });
  }

  function initializeSelector(control) {
    if (control.dataset.mrjCurrencyInitialized === 'true') return;
    control.dataset.mrjCurrencyInitialized = 'true';
    const trigger = control.querySelector('[data-mrj-currency-trigger]');
    trigger.addEventListener('click', (event) => {
      event.stopPropagation();
      if (trigger.disabled) return;
      if (control.classList.contains('active')) closeSelector(control, false);
      else openSelector(control, false);
    });
    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        openSelector(control, event.key === 'ArrowDown' ? 'first' : 'last');
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeSelector(control, false);
      }
    });
    control.querySelector('[data-mrj-currency-menu]').addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        focusOption(control, event.key === 'ArrowDown' ? 1 : -1);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        focusOption(control, event.key === 'Home' ? 'first' : 'last');
      } else if (event.key === 'Escape' || event.key === 'Tab') {
        closeSelector(control, event.key === 'Escape');
      }
    });
    renderSelector(control);
  }

  function initializeSelectors(root) {
    const controls = [];
    if (root instanceof Element && root.matches(selectorQuery)) controls.push(root);
    if (root.querySelectorAll) controls.push(...root.querySelectorAll(selectorQuery));
    controls.forEach(initializeSelector);
    if (!outsideClickStarted) {
      outsideClickStarted = true;
      document.addEventListener('click', () => {
        document.querySelectorAll(selectorQuery + '.active').forEach((control) => closeSelector(control, false));
      });
    }
  }

  function publishContext(emitChange) {
    window.MRJCurrency = {
      snapshotId: snapshot.id,
      countryCode: String(config.countryCode || 'IN').toUpperCase(),
      currencyCode: currentContext.currencyCode,
      rate: currentContext.rate,
      selectionMode: currentContext.selectionMode
    };
    document.documentElement.dataset.mrjCurrency = currentContext.currencyCode;
    if (emitChange) {
      document.dispatchEvent(new CustomEvent('mrj:currency-changed', {
        detail: Object.assign({}, window.MRJCurrency)
      }));
    }
  }

  function refresh(emitChange) {
    currentContext = contextFor(snapshot);
    apply(document);
    initializeSelectors(document);
    document.querySelectorAll(selectorQuery).forEach(renderSelector);
    publishContext(emitChange);
  }

  function markSelectorsUnavailable() {
    initializeSelectors(document);
    document.querySelectorAll(selectorQuery).forEach((control) => {
      const trigger = control.querySelector('[data-mrj-currency-trigger]');
      trigger.disabled = true;
      trigger.setAttribute('aria-label', 'Currency selection unavailable');
      trigger.querySelector('[data-mrj-currency-trigger-label]').textContent = String(config.storeCurrency || 'INR').toUpperCase();
      control.querySelector('[data-mrj-currency-options]').replaceChildren();
      closeSelector(control, false);
    });
  }

  function observePrices() {
    if (observerStarted) return;
    observerStarted = true;
    new MutationObserver((records) => {
      if (processing) return;
      records.forEach((record) => {
        if (record.type === 'attributes') apply(record.target);
        record.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          initializeSelectors(node);
          apply(node);
        });
      });
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-mrj-base-minor']
    });
  }

  async function load() {
    const url = config.snapshotUrl || config.fallbackUrl;
    try {
      const cached = cachedSnapshot();
      if (cached) {
        window.MRJCurrencySnapshot = cached;
        snapshot = effectiveSnapshot(cached);
        refresh(true);
        observePrices();
        return;
      }
      if (!url) throw new Error('Snapshot URL is unavailable');

      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), fetchTimeoutMs);
      let response;
      try {
        response = await fetch(url, { cache: 'no-cache', signal: controller.signal });
      } finally {
        window.clearTimeout(timeout);
      }
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const downloadedSnapshot = await response.json();
      if (!isValidSnapshot(downloadedSnapshot)) throw new Error('Snapshot validation failed');
      window.MRJCurrencySnapshot = downloadedSnapshot;
      cacheSnapshot(downloadedSnapshot);
      snapshot = effectiveSnapshot(downloadedSnapshot);
      refresh(true);
      observePrices();
    } catch (error) {
      markSelectorsUnavailable();
      console.warn('[MRJ Currency] Unable to load snapshot; retaining INR prices.', error);
    } finally {
      revealPrices();
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', load);
  else load();
})();
